import React, {useContext, useState} from 'react'
import AppContext from '../appContext'
import GeoMap, {SamplePoint} from './GeoMap'
import QCMapSidebar from './QCMapSidebar'

export interface QCMapPanelProps {
    samplePoints: SamplePoint[];
    boundaryCoordinates?: Array<{x: number; y: number}>;
    height?: string;
    boundaryError?: boolean;
    landId?: number;
    selectedPointLabel?: string | null;
    onPointSelect?: (label: string | null) => void;
    children?: React.ReactNode;
}

/**
 * Combined map and sidebar panel with built-in hover synchronization.
 * Encapsulates the bidirectional hover highlighting between map points and sidebar list.
 * Also renders boundary status legend and error messages.
 */
const QCMapPanel: React.FC<QCMapPanelProps> = ({
    samplePoints,
    boundaryCoordinates,
    height = "60vh",
    boundaryError,
    landId,
    selectedPointLabel,
    onPointSelect,
    children
}) => {
    const app = useContext(AppContext)
    const [hoveredSampleLabel, setHoveredSampleLabel] = useState<string | null>(null)

    const hasBoundary = boundaryCoordinates && boundaryCoordinates.length > 0

    return (
        <div>
            <div className="flex" style={{height}}>
                <div className="flex-1 relative overflow-hidden">
                    <GeoMap
                        boundaryCoordinates={boundaryCoordinates}
                        samplePoints={samplePoints}
                        height="100%"
                        highlightedPointLabel={selectedPointLabel ?? hoveredSampleLabel}
                        onPointClick={onPointSelect ? (point) => {
                            const label = point.label || null
                            onPointSelect(label === selectedPointLabel ? null : label)
                        } : undefined}
                        onPointHover={(point) => setHoveredSampleLabel(point.label || null)}
                        onPointLeave={() => setHoveredSampleLabel(null)}
                        distinguishSubsoil
                    />
                    {children}
                </div>
                <QCMapSidebar
                    samplePoints={samplePoints}
                    boundaryCoordinates={boundaryCoordinates}
                    hoveredSampleLabel={hoveredSampleLabel}
                    onSampleHover={setHoveredSampleLabel}
                    onSampleLeave={() => setHoveredSampleLabel(null)}
                    selectedSampleLabel={selectedPointLabel}
                    onSampleClick={onPointSelect ? (label) => {
                        onPointSelect(label === selectedPointLabel ? null : label)
                    } : undefined}
                />
            </div>
            <div className="flex mt-2 text-sm text-gray-600">
                {boundaryError && (
                    <span className="mr-4 text-red-500">
                        {app.word('boundary_load_error')}
                    </span>
                )}
                {hasBoundary && (
                    <span className="mr-4">
                        <span className="inline-block w-3 h-3 bg-primary-500 mr-1"></span>
                        {app.word('boundary')}
                    </span>
                )}
                {landId && !boundaryError && !hasBoundary && (
                    <span className="mr-4 text-orange-500">
                        {app.word('no_boundary_data')}
                    </span>
                )}
                {samplePoints.some(p => p.sampleDepth === 'subsoil') && (
                    <>
                        <span className="flex items-center mr-4">
                            <span className="inline-block w-3 h-3 rounded-full mr-1" style={{backgroundColor: '#537425'}}></span>
                            {app.word('TopSoil')}
                        </span>
                        <span className="flex items-center">
                            <span className="inline-block w-3 h-3 rounded-full mr-1" style={{backgroundColor: '#9CA3AF'}}></span>
                            {app.word('SubSoil')}
                        </span>
                    </>
                )}
            </div>
        </div>
    )
}

export default QCMapPanel
